import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { alpha, useTheme } from "@mui/material/styles";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import OpenInNewOutlinedIcon from "@mui/icons-material/OpenInNewOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import StopOutlinedIcon from "@mui/icons-material/StopOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import TravelExploreOutlinedIcon from "@mui/icons-material/TravelExploreOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import LanguageOutlinedIcon from "@mui/icons-material/LanguageOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import {
  cancelSkillAgentSearch,
  clearSkillAgentSearchPhotos,
  deleteSkillAgentSearch,
  deleteSkillAgentSearchItems,
  fetchSkillAgentSearchItemBlobUrl,
  getSkillAgentSearchById,
  getSkillAgentSearchesMeta,
  getSkillAgentScrapingBeeCredits,
  listSkillAgentSearchItems,
  listSkillAgentSearchRuns,
  rerunSkillAgentSearchRun,
  runSkillAgentSearch,
  updateSkillAgentSearch,
} from "../../api/Skills/skillAgentSearchesApi";
import {
  addSkillAgentMoodboardAssets,
  createSkillAgentMoodboard,
} from "../../api/Skills/skillAgentMoodboardsApi";
import {
  pageFieldSx,
  pagePrimaryButtonSx,
} from "../../styles/pageModernStyles";
import {
  bob,
  cloudLift,
  motionSafe,
  rise,
  staggerIn,
  studioDialogBackdropSx,
  studioDialogPaperSx,
} from "../AI/studioDialogMotion";
import { searchStatusLabel } from "./skillAgentTaskTree";
import SkillAgentBrandSourcesFields, {
  buildDefaultUrlSelection,
  healUrlSelection,
} from "./skillAgentBrandSourcesFields";
import SkillAgentCustomSourcesFields, {
  customSourcesFromLegacy,
  customSourcesToUrls,
  normalizeCustomSources,
} from "./skillAgentCustomSourcesFields";
import SkillAgentResearchCatalogDialog from "./SkillAgentResearchCatalogDialog";
import SkillAgentInlineTitle from "./SkillAgentInlineTitle";
import SkillAgentScrapingBeeCreditsAlert from "./SkillAgentScrapingBeeCreditsAlert";

const SEARCH_ACCENT = "#00897b";
const DANGER_ACCENT = "#e53935";
const WARN_ACCENT = "#f57c00";

/** Confirmación tipo studio (blur + rise) para borrar fotos / SEARCH. */
function SearchConfirmDialog({
  open,
  onClose,
  accent = DANGER_ACCENT,
  icon: Icon = DeleteOutlineIcon,
  eyebrow,
  title,
  body,
  countLabel,
  cancelLabel,
  confirmLabel,
  confirmColor = "error",
  busy = false,
  onConfirm,
  confirmDisabled = false,
}) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  return (
    <Dialog
      open={open}
      onClose={() => !busy && onClose?.()}
      fullWidth
      maxWidth="xs"
      keepMounted={false}
      transitionDuration={{ enter: 280, exit: 160 }}
      slotProps={{ backdrop: { sx: studioDialogBackdropSx } }}
      BackdropProps={{ sx: studioDialogBackdropSx }}
      PaperProps={{
        sx: {
          ...studioDialogPaperSx,
          borderRadius: 3.5,
          borderColor: alpha(accent, 0.28),
          boxShadow: `0 28px 64px ${alpha(accent, 0.22)}, 0 8px 24px rgba(15,23,42,0.18)`,
          background: isDark
            ? `linear-gradient(165deg, ${alpha(accent, 0.14)} 0%, ${theme.palette.background.paper} 44%)`
            : `linear-gradient(165deg, ${alpha(accent, 0.1)} 0%, #fff 50%)`,
          animation: open
            ? `${rise} 420ms cubic-bezier(0.22, 1, 0.36, 1) both`
            : undefined,
          ...motionSafe,
        },
      }}
    >
      <Box
        sx={{
          position: "relative",
          px: 2.5,
          pt: 2.25,
          pb: 1.5,
          overflow: "hidden",
          borderBottom: "1px solid",
          borderColor: alpha(accent, 0.16),
        }}
      >
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            background: `radial-gradient(ellipse at 12% 0%, ${alpha(
              accent,
              0.24,
            )} 0%, transparent 55%)`,
            pointerEvents: "none",
          }}
        />
        <Stack
          direction="row"
          spacing={1.25}
          alignItems="flex-start"
          sx={{
            position: "relative",
            animation: `${staggerIn} 360ms cubic-bezier(0.22, 1, 0.36, 1) both`,
            ...motionSafe,
          }}
        >
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              flexShrink: 0,
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.16),
              color: accent,
              border: `1px solid ${alpha(accent, 0.32)}`,
              animation: `${cloudLift} 2.6s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <Icon sx={{ fontSize: 24 }} />
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            {eyebrow ? (
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 800,
                  letterSpacing: 0.6,
                  textTransform: "uppercase",
                  color: accent,
                }}
              >
                {eyebrow}
              </Typography>
            ) : null}
            <Typography
              variant="h6"
              sx={{ fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.2 }}
            >
              {title}
            </Typography>
          </Box>
        </Stack>
        <Box
          aria-hidden
          sx={{
            position: "relative",
            height: 56,
            mt: 1.25,
            borderRadius: 2,
            overflow: "hidden",
            bgcolor: alpha(accent, 0.08),
            border: `1px solid ${alpha(accent, 0.2)}`,
          }}
        >
          {[0, 1, 2].map((i) => (
            <Box
              key={i}
              sx={{
                position: "absolute",
                left: `${18 + i * 22}%`,
                top: 10 + (i % 2) * 6,
                width: 28,
                height: 34,
                borderRadius: 1,
                bgcolor: alpha("#fff", isDark ? 0.08 : 0.9),
                border: `1.5px solid ${alpha(accent, 0.35)}`,
                animation: `${bob} ${1.9 + i * 0.15}s ease-in-out infinite`,
                animationDelay: `${i * 0.08}s`,
                ...motionSafe,
              }}
            />
          ))}
          <Box
            sx={{
              position: "absolute",
              right: 14,
              top: 12,
              width: 32,
              height: 32,
              borderRadius: "50%",
              display: "grid",
              placeItems: "center",
              bgcolor: alpha(accent, 0.18),
              color: accent,
              animation: `${cloudLift} 2.4s ease-in-out infinite`,
              ...motionSafe,
            }}
          >
            <Icon sx={{ fontSize: 18 }} />
          </Box>
        </Box>
      </Box>
      <DialogContent sx={{ pt: 2, px: 2.5 }}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            lineHeight: 1.5,
            animation: `${staggerIn} 320ms ease both`,
            ...motionSafe,
          }}
        >
          {body}
        </Typography>
        {countLabel ? (
          <Typography
            variant="body2"
            fontWeight={800}
            sx={{ mt: 1.25, color: accent }}
          >
            {countLabel}
          </Typography>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 2.5, pb: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={{ textTransform: "none" }}
        >
          {cancelLabel}
        </Button>
        <Button
          color={confirmColor}
          variant="contained"
          disabled={busy || confirmDisabled}
          onClick={onConfirm}
          startIcon={
            busy ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <Icon fontSize="small" />
            )
          }
          sx={{ textTransform: "none", fontWeight: 800, borderRadius: 2 }}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function SearchParamsStage({ accent }) {
  return (
    <Box
      aria-hidden
      sx={{
        position: "relative",
        height: 84,
        mt: 1.1,
        mb: 0.25,
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: alpha(accent, 0.08),
        border: "1px solid",
        borderColor: alpha(accent, 0.22),
        animation: `${rise} 320ms ease both`,
        ...motionSafe,
      }}
    >
      <Box
        sx={{
          position: "absolute",
          left: "16%",
          top: 16,
          width: 46,
          height: 50,
          borderRadius: 1.5,
          bgcolor: "#fff",
          border: "1.5px solid",
          borderColor: alpha(accent, 0.4),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 6px 14px ${alpha(accent, 0.14)}`,
          animation: `${bob} 2.2s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <TravelExploreOutlinedIcon sx={{ fontSize: 24, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          left: "44%",
          top: 22,
          width: 42,
          height: 42,
          borderRadius: 1.25,
          bgcolor: alpha(accent, 0.12),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.45),
          display: "grid",
          placeItems: "center",
          animation: `${bob} 2.5s ease-in-out infinite`,
          animationDelay: "0.12s",
          ...motionSafe,
        }}
      >
        <LanguageOutlinedIcon sx={{ fontSize: 22, color: accent }} />
      </Box>
      <Box
        sx={{
          position: "absolute",
          right: "14%",
          top: 12,
          width: 52,
          height: 52,
          borderRadius: 1.5,
          bgcolor: alpha(accent, 0.16),
          border: "1.5px solid",
          borderColor: alpha(accent, 0.5),
          display: "grid",
          placeItems: "center",
          boxShadow: `0 8px 18px ${alpha(accent, 0.18)}`,
          animation: `${cloudLift} 2.4s ease-in-out infinite`,
          ...motionSafe,
        }}
      >
        <TuneOutlinedIcon sx={{ fontSize: 28, color: accent }} />
      </Box>
      {[0, 1, 2].map((i) => (
        <Box
          key={i}
          sx={{
            position: "absolute",
            left: `${34 + i * 9}%`,
            bottom: 12,
            width: 7,
            height: 7,
            borderRadius: "50%",
            bgcolor: alpha(accent, 0.3 + i * 0.15),
            animation: `${bob} ${1.8 + i * 0.2}s ease-in-out infinite`,
            animationDelay: `${i * 0.1}s`,
            ...motionSafe,
          }}
        />
      ))}
    </Box>
  );
}

function SearchThumb({ item, onOpen, selected, onToggleSelect, selectable }) {
  const { t } = useTranslation();
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let revoked = false;
    let objectUrl = "";
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item.id]);

  return (
    <Box
      onClick={() => src && onOpen?.(item)}
      sx={{
        position: "relative",
        borderRadius: 2.25,
        overflow: "hidden",
        border: "1px solid",
        borderColor: selected ? "error.main" : alpha("#0f172a", 0.08),
        bgcolor: "background.paper",
        cursor: src ? "zoom-in" : "default",
        transition:
          "border-color 0.15s ease, box-shadow 0.15s ease, transform 0.15s ease",
        "&:hover": src
          ? {
              borderColor: selected ? "error.main" : "primary.main",
              boxShadow: (t) =>
                `0 8px 22px ${alpha(t.palette.primary.main, 0.16)}`,
              transform: "translateY(-1px)",
            }
          : undefined,
      }}
    >
      {selectable ? (
        <Checkbox
          size="small"
          checked={!!selected}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => {
            e.stopPropagation();
            onToggleSelect?.(item, e.target.checked);
          }}
          sx={{
            position: "absolute",
            top: 4,
            left: 4,
            zIndex: 2,
            p: 0.25,
            bgcolor: alpha("#fff", 0.9),
            borderRadius: 1,
            boxShadow: `0 1px 4px ${alpha("#0f172a", 0.12)}`,
            "&:hover": { bgcolor: "#fff" },
          }}
          inputProps={{
            "aria-label": t("skillAgent.search.ariaSelectPhoto"),
          }}
        />
      ) : null}
      <Box
        sx={{
          height: 168,
          bgcolor: alpha("#0f172a", 0.03),
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
            }}
            onError={() => setFailed(true)}
          />
        ) : failed ? (
          <Typography variant="caption" color="text.secondary">
            {t("skillAgent.common.noPreview")}
          </Typography>
        ) : (
          <CircularProgress size={18} />
        )}
      </Box>
      <Box
        sx={{
          px: 1,
          py: 0.7,
          borderTop: "1px solid",
          borderColor: alpha("#0f172a", 0.06),
          bgcolor: alpha("#fff", 0.92),
        }}
      >
        <Typography
          variant="caption"
          fontWeight={700}
          noWrap
          display="block"
          sx={{ letterSpacing: 0.01 }}
        >
          {item.Title || item.BrandName || "img"}
        </Typography>
      </Box>
    </Box>
  );
}

function SearchPhotoLightbox({
  items,
  index,
  onClose,
  onIndexChange,
  onDeleteCurrent,
  deleting,
}) {
  const { t } = useTranslation();
  const item = index != null && items[index] ? items[index] : null;
  const [src, setSrc] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!item?.id) {
      setSrc("");
      return undefined;
    }
    let revoked = false;
    let objectUrl = "";
    setSrc("");
    setFailed(false);
    (async () => {
      try {
        objectUrl = await fetchSkillAgentSearchItemBlobUrl(item.id);
        if (!revoked) setSrc(objectUrl);
      } catch {
        if (!revoked) {
          setSrc("");
          setFailed(true);
        }
      }
    })();
    return () => {
      revoked = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [item?.id]);

  useEffect(() => {
    if (item == null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") onIndexChange?.(Math.max(0, index - 1));
      if (e.key === "ArrowRight")
        onIndexChange?.(Math.min(items.length - 1, index + 1));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [item, index, items.length, onClose, onIndexChange]);

  if (!item) return null;

  const driveHref = item.DriveFileUrl || null;
  const sourceHref = item.SourceUrl || null;
  const canPrev = index > 0;
  const canNext = index < items.length - 1;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: "background.paper",
          overflow: "hidden",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          px: 1.5,
          py: 1,
          borderBottom: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="subtitle1" fontWeight={800} noWrap>
            {item.Title ||
              item.BrandName ||
              t("skillAgent.search.lightboxPhoto")}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[item.BrandName, `${index + 1} / ${items.length}`]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        {sourceHref ? (
          <Button
            size="small"
            component={Link}
            href={sourceHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.search.openSource")}
          </Button>
        ) : null}
        {driveHref ? (
          <Button
            size="small"
            component={Link}
            href={driveHref}
            target="_blank"
            rel="noopener noreferrer"
            startIcon={<OpenInNewOutlinedIcon />}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.common.drive")}
          </Button>
        ) : null}
        {onDeleteCurrent ? (
          <Button
            size="small"
            color="error"
            disabled={deleting}
            startIcon={
              deleting ? (
                <CircularProgress size={12} color="inherit" />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            onClick={() => onDeleteCurrent(item)}
            sx={{ textTransform: "none", flexShrink: 0 }}
          >
            {t("skillAgent.common.delete")}
          </Button>
        ) : null}
        <IconButton
          onClick={onClose}
          size="small"
          aria-label={t("skillAgent.common.close")}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      <DialogContent
        sx={{
          p: 0,
          position: "relative",
          bgcolor: alpha("#000", 0.92),
          minHeight: { xs: 320, md: 560 },
          display: "grid",
          placeItems: "center",
        }}
      >
        {src ? (
          <Box
            component="img"
            src={src}
            alt={item.Title || item.BrandName || ""}
            sx={{
              maxWidth: "100%",
              maxHeight: { xs: "70vh", md: "78vh" },
              objectFit: "contain",
              display: "block",
            }}
          />
        ) : failed ? (
          <Typography color="grey.400">
            {t("skillAgent.search.lightboxLoadError")}
          </Typography>
        ) : (
          <CircularProgress sx={{ color: "grey.200" }} />
        )}
        <IconButton
          disabled={!canPrev}
          onClick={() => onIndexChange?.(index - 1)}
          sx={{
            position: "absolute",
            left: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label={t("skillAgent.search.lightboxPrev")}
        >
          <ChevronLeftIcon />
        </IconButton>
        <IconButton
          disabled={!canNext}
          onClick={() => onIndexChange?.(index + 1)}
          sx={{
            position: "absolute",
            right: 8,
            top: "50%",
            transform: "translateY(-50%)",
            bgcolor: alpha("#fff", 0.12),
            color: "#fff",
            "&:hover": { bgcolor: alpha("#fff", 0.22) },
            "&.Mui-disabled": { color: alpha("#fff", 0.25) },
          }}
          aria-label={t("skillAgent.search.lightboxNext")}
        >
          <ChevronRightIcon />
        </IconButton>
      </DialogContent>
    </Dialog>
  );
}

export default function SkillAgentSearchDetail({
  searchId,
  onChanged,
  onDeleted,
  onMoodboardCreated,
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [okMsg, setOkMsg] = useState("");
  const [search, setSearch] = useState(null);
  const [titulo, setTitulo] = useState("");
  const [items, setItems] = useState([]);
  const [running, setRunning] = useState(false);
  const [busy, setBusy] = useState(false);
  const [creatingMoodboard, setCreatingMoodboard] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  const [selectedDeleteOpen, setSelectedDeleteOpen] = useState(false);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [catalogBrands, setCatalogBrands] = useState([]);
  const [scrapingBeeStatus, setScrapingBeeStatus] = useState(null);
  const [editForm, setEditForm] = useState({
    Titulo: "",
    Tipologia: "",
    brandIds: [],
    urlSelection: {},
    customSources: [],
    maxImagesPerBrand: "3",
    maxAgeDays: "60",
  });
  const [runs, setRuns] = useState([]);
  const [runsLoading, setRunsLoading] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [showSourceDetail, setShowSourceDetail] = useState(false);
  const [showRuns, setShowRuns] = useState(false);

  const images = useMemo(
    () => (Array.isArray(items) ? items.filter((i) => i.Kind === "image") : []),
    [items],
  );

  const runStats = useMemo(() => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : [];
    const customSources =
      Array.isArray(c.customSources) && c.customSources.length
        ? normalizeCustomSources(c.customSources).filter((s) => s.enabled)
        : customSourcesFromLegacy(c.customUrls);
    const perBrand = c.maxImagesPerBrand || 3;
    const ageDays = c.maxAgeDays != null ? c.maxAgeDays : 60;
    const nSources = brands.length + customSources.length;
    const latest = Array.isArray(runs) && runs[0] ? runs[0] : null;
    const statuses =
      latest?.SummaryJson && Array.isArray(latest.SummaryJson.sourceStatuses)
        ? latest.SummaryJson.sourceStatuses
        : [];
    const withHits = statuses.filter((s) => (s.passed || 0) > 0);
    const withErr = statuses.filter(
      (s) => s.status === "error" || s.status === "no_scrapeable",
    );
    const skippedMatch = String(search?.ResultSummary || "").match(
      /(\d+)\s*ya bajadas/,
    );
    const addedMatch = String(search?.ResultSummary || "").match(
      /\+(\d+)\s*fotos/,
    );
    return {
      nSources,
      ageDays,
      perBrand,
      statuses,
      withHits,
      withErr,
      skipped: skippedMatch ? skippedMatch[1] : null,
      added: addedMatch ? addedMatch[1] : null,
    };
  }, [search, runs]);

  const selectedCount = selectedIds.size;

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchId]);

  useEffect(() => {
    const valid = new Set(images.map((i) => Number(i.id)));
    setSelectedIds((prev) => {
      const next = new Set([...prev].filter((id) => valid.has(id)));
      return next.size === prev.size ? prev : next;
    });
  }, [images]);

  const toggleSelect = useCallback((item, on) => {
    const id = Number(item.id);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const selectAllPhotos = () => {
    setSelectedIds(new Set(images.map((i) => Number(i.id))));
  };

  const clearSelection = () => setSelectedIds(new Set());

  const load = useCallback(async () => {
    if (!searchId) return;
    setLoading(true);
    setError("");
    try {
      const data = await getSkillAgentSearchById(searchId);
      setSearch(data);
      setTitulo(data?.Titulo || "");
      setItems(Array.isArray(data?.items) ? data.items : []);
      setRunning(data?.Status === "running");
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.loadError"));
    } finally {
      setLoading(false);
    }
  }, [searchId]);

  const loadRuns = useCallback(async () => {
    if (!searchId) return;
    setRunsLoading(true);
    try {
      const data = await listSkillAgentSearchRuns(searchId);
      setRuns(Array.isArray(data?.runs) ? data.runs : []);
    } catch {
      setRuns([]);
    } finally {
      setRunsLoading(false);
    }
  }, [searchId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    const customerId = search?.CustomerId;
    getSkillAgentSearchesMeta(
      customerId ? { customerId: Number(customerId) } : {},
    )
      .then((m) => {
        const list = Array.isArray(m?.brands) ? m.brands : [];
        setCatalogBrands(
          [...list].sort((a, b) =>
            String(a.name || a.id).localeCompare(String(b.name || b.id), "es", {
              sensitivity: "base",
            }),
          ),
        );
        if (m?.scrapingBee) setScrapingBeeStatus(m.scrapingBee);
      })
      .catch(() => setCatalogBrands([]));
  }, [search?.CustomerId]);

  useEffect(() => {
    if (!searchId || !running) return undefined;
    const pollTimer = setInterval(async () => {
      try {
        const data = await listSkillAgentSearchItems(searchId);
        if (data?.status && data.status !== "running") {
          setRunning(false);
          await load();
          await loadRuns();
          onChanged?.();
          getSkillAgentScrapingBeeCredits()
            .then((s) => setScrapingBeeStatus(s))
            .catch(() => {});
          if (data.status === "done") {
            const summary = String(data.resultSummary || "");
            const added = summary.match(/\+(\d+)\s*fotos/);
            const skipped = summary.match(/(\d+)\s*ya bajadas/);
            setOkMsg(
              t("skillAgent.search.pollDoneMsg", {
                n: added ? added[1] : 0,
                skipped: skipped ? skipped[1] : 0,
              }),
            );
          } else if (data.status === "error") {
            setError(
              String(
                data.resultSummary || t("skillAgent.search.pollError"),
              ).slice(0, 180),
            );
          }
        }
      } catch {
        /* keep polling */
      }
    }, 4000);
    return () => clearInterval(pollTimer);
  }, [searchId, running, load, loadRuns, onChanged, t]);

  const openEdit = () => {
    const c =
      search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
        ? search.ConstraintsJson
        : {};
    const brands = Array.isArray(c.researchBrands) ? c.researchBrands : null;
    // [] explícito = sin marcas GU (solo webs propias). No volver a marcar todas.
    const brandIds = brands != null ? brands : catalogBrands.map((b) => b.id);
    const customSources =
      Array.isArray(c.customSources) && c.customSources.length
        ? normalizeCustomSources(c.customSources)
        : customSourcesFromLegacy(c.customUrls);
    const urlSelectionRaw =
      c.brandUrlSelection && typeof c.brandUrlSelection === "object"
        ? c.brandUrlSelection
        : buildDefaultUrlSelection(catalogBrands, brandIds);
    const tipologia = search?.Tipologia || c.tipología || "";
    const urlSelection = healUrlSelection(
      urlSelectionRaw,
      catalogBrands,
      brandIds,
      tipologia,
    );
    setEditForm({
      Titulo: search?.Titulo || "",
      Tipologia: tipologia,
      brandIds,
      urlSelection,
      customSources,
      maxImagesPerBrand: String(c.maxImagesPerBrand || 3),
      maxAgeDays: String(c.maxAgeDays != null ? c.maxAgeDays : 60),
    });
    setEditOpen(true);
  };

  const handleSaveParams = async ({ andRun = false } = {}) => {
    setBusy(true);
    setError("");
    setOkMsg("");
    try {
      const tipology = String(editForm.Tipologia || "").trim();
      if (!tipology) {
        setError(t("skillAgent.create.tipologyRequired"));
        setBusy(false);
        return;
      }
      const customSources = normalizeCustomSources(editForm.customSources);
      const customUrls = customSourcesToUrls(customSources);
      const researchBrands = Array.isArray(editForm.brandIds)
        ? editForm.brandIds
        : [];
      if (
        !researchBrands.length &&
        !customSources.filter((s) => s.enabled).length
      ) {
        setError(t("skillAgent.search.needBrandOrUrl"));
        setBusy(false);
        return;
      }
      const prev =
        search?.ConstraintsJson && typeof search.ConstraintsJson === "object"
          ? search.ConstraintsJson
          : {};
      const updated = await updateSkillAgentSearch(searchId, {
        Titulo: editForm.Titulo.trim() || search.Titulo,
        Tipologia: tipology,
        ConstraintsJson: {
          ...prev,
          tipología: tipology,
          researchBrands,
          brandUrlSelection: healUrlSelection(
            editForm.urlSelection || {},
            catalogBrands,
            researchBrands,
            tipology,
          ),
          customSources,
          customUrls,
          maxImagesPerBrand: Number(editForm.maxImagesPerBrand) || 3,
          maxAgeDays: Number(editForm.maxAgeDays) || 60,
          preferNewest: prev.preferNewest !== false,
          skipKnown: prev.skipKnown !== false,
          skipKnownScope: "clienteTemporada",
        },
      });
      setSearch((p) => ({ ...p, ...updated }));
      setTitulo(updated?.Titulo || editForm.Titulo.trim() || search.Titulo);
      setEditOpen(false);
      setOkMsg(t("skillAgent.search.paramsSaved"));
      onChanged?.();
      if (andRun) {
        await runSkillAgentSearch(searchId, {
          customerFolderName: search?.CustomerName || undefined,
        });
        setRunning(true);
        setSearch((p) => (p ? { ...p, Status: "running" } : p));
        setOkMsg(t("skillAgent.search.rerunNewParams"));
      }
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.saveError"));
    } finally {
      setBusy(false);
    }
  };

  const handleRerun = async (run) => {
    setError("");
    setOkMsg("");
    try {
      await rerunSkillAgentSearchRun(searchId, run.id, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      setOkMsg(t("skillAgent.search.rerunSameParams"));
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.runError"));
    }
  };

  const handleRun = async () => {
    setError("");
    setOkMsg("");
    try {
      await runSkillAgentSearch(searchId, {
        customerFolderName: search?.CustomerName || undefined,
      });
      setRunning(true);
      setSearch((p) => (p ? { ...p, Status: "running" } : p));
      onChanged?.();
    } catch (err) {
      setError(err?.response?.data?.error || t("skillAgent.search.runError"));
    }
  };

  const handleCancel = async () => {
    try {
      await cancelSkillAgentSearch(searchId);
      setRunning(false);
      await load();
      onChanged?.();
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.cancelError"),
      );
    }
  };

  const handleClearPhotos = async () => {
    setBusy(true);
    setError("");
    try {
      await clearSkillAgentSearchPhotos(searchId);
      setClearOpen(false);
      setSelectedIds(new Set());
      await load();
      onChanged?.();
      setOkMsg(t("skillAgent.search.photosCleared"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.deletePhotosError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const applyDeletedItems = (data, deletedIds) => {
    if (Array.isArray(data?.items)) {
      setItems(data.items);
    } else {
      const gone = new Set(deletedIds.map(Number));
      setItems((prev) => prev.filter((i) => !gone.has(Number(i.id))));
    }
    if (data?.search) setSearch(data.search);
    setSelectedIds((prev) => {
      const gone = new Set(deletedIds.map(Number));
      return new Set([...prev].filter((id) => !gone.has(id)));
    });
  };

  const handleDeleteSelected = async () => {
    const ids = [...selectedIds];
    if (!ids.length) return;
    // Cierra ya y quita del grid (Drive tarda; no hay que mirar el dialog girando).
    setSelectedDeleteOpen(false);
    applyDeletedItems({}, ids);
    setBusy(true);
    setError("");
    try {
      const data = await deleteSkillAgentSearchItems(searchId, ids);
      applyDeletedItems(data, data?.deletedIds || ids);
      onChanged?.();
      setOkMsg(
        t("skillAgent.search.photosDeleted", {
          n: data?.deleted ?? ids.length,
        }),
      );
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.search.deleteSelectedPhotosError"),
      );
      await load();
    } finally {
      setBusy(false);
    }
  };

  const handleDeleteLightboxItem = async (item) => {
    if (!item?.id) return;
    setBusy(true);
    setError("");
    try {
      const id = Number(item.id);
      const data = await deleteSkillAgentSearchItems(searchId, [id]);
      const remaining = Array.isArray(data?.items)
        ? data.items.filter((i) => i.Kind === "image")
        : images.filter((i) => Number(i.id) !== id);
      applyDeletedItems(data, [id]);
      onChanged?.();
      if (!remaining.length) {
        setLightboxIndex(null);
      } else {
        const nextIdx = Math.min(lightboxIndex ?? 0, remaining.length - 1);
        setLightboxIndex(nextIdx);
      }
      setOkMsg(t("skillAgent.search.photoDeleted"));
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.deletePhotosError"),
      );
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    setError("");
    try {
      await deleteSkillAgentSearch(searchId);
      setDeleteOpen(false);
      onDeleted?.(searchId);
    } catch (err) {
      setError(
        err?.response?.data?.error || t("skillAgent.search.deleteError"),
      );
      setBusy(false);
    }
  };

  if (loading && !search) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (!search) {
    return (
      <Alert severity="warning" sx={{ m: 2 }}>
        {t("skillAgent.search.notFound")}
      </Alert>
    );
  }

  const locked = running || busy || creatingMoodboard;
  const tipologiaShort = search.Tipologia
    ? String(search.Tipologia).length > 48
      ? `${String(search.Tipologia).slice(0, 48)}…`
      : search.Tipologia
    : null;
  const metaChipSx = {
    height: 24,
    borderRadius: 1.25,
    fontWeight: 700,
    bgcolor: (t) => alpha(t.palette.primary.main, 0.06),
    borderColor: (t) => alpha(t.palette.primary.main, 0.14),
    "& .MuiChip-label": { px: 0.9, fontSize: "0.72rem" },
  };
  const toolBtnSx = {
    width: 34,
    height: 34,
    borderRadius: 2,
    border: "1px solid",
    borderColor: "divider",
    bgcolor: alpha("#fff", 0.7),
    "&:hover": { bgcolor: (t) => alpha(t.palette.primary.main, 0.08) },
  };

  const handleCreateMoodboardFromSearch = async () => {
    if (!search || creatingMoodboard) return;
    const selectedImageIds = [...selectedIds].filter((id) =>
      images.some((img) => Number(img.id) === Number(id)),
    );
    const searchItemIds = (
      selectedImageIds.length
        ? selectedImageIds
        : images.map((img) => Number(img.id))
    ).filter((id) => Number.isFinite(id) && id > 0);
    if (!searchItemIds.length) {
      setError(t("skillAgent.search.createMoodboardNeedPhotos"));
      return;
    }
    setCreatingMoodboard(true);
    setError("");
    try {
      const boardTitle = String(titulo || search.Titulo || "Moodboard").trim();
      const created = await createSkillAgentMoodboard({
        Titulo: boardTitle,
        CategoryLabel: boardTitle,
        Temporada: search.Temporada || null,
        CustomerId: search.CustomerId || null,
        CustomerName: search.CustomerName || null,
        ProjectId: search.ProjectId || null,
        MeetingSessionId: search.MeetingSessionId || null,
        Kind: "moodboard",
      });
      if (created?.id) {
        await addSkillAgentMoodboardAssets(created.id, { searchItemIds });
      }
      onChanged?.();
      if (created?.id) {
        onMoodboardCreated?.(Number(created.id), {
          ProjectId: created.ProjectId || search.ProjectId || null,
          MeetingSessionId:
            created.MeetingSessionId || search.MeetingSessionId || null,
        });
      }
    } catch (err) {
      setError(
        err?.response?.data?.error ||
          t("skillAgent.search.createMoodboardError"),
      );
    } finally {
      setCreatingMoodboard(false);
    }
  };

  return (
    <Box
      sx={{
        flex: 1,
        alignSelf: "stretch",
        width: "100%",
        height: "100%",
        maxHeight: "100%",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        backgroundImage: (t) =>
          `radial-gradient(900px 280px at 0% -30%, ${alpha(t.palette.primary.main, 0.07)}, transparent)`,
      }}
    >
      <SkillAgentScrapingBeeCreditsAlert
        initial={scrapingBeeStatus}
        sx={{
          flexShrink: 0,
          mx: { xs: 1.5, md: 2 },
          mt: { xs: 1.5, md: 2 },
          mb: 1,
        }}
      />
      <Box
        sx={{
          flexShrink: 0,
          zIndex: 8,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 1,
          mx: { xs: 1.5, md: 2 },
          mt: { xs: 1.5, md: 2 },
          py: 0.85,
          px: { xs: 0.75, md: 1 },
          borderRadius: 2.5,
          border: "1px solid",
          borderColor: (t) => alpha(t.palette.primary.main, 0.12),
          bgcolor: (t) => alpha(t.palette.background.paper, 0.96),
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          boxShadow: `0 6px 20px ${alpha("#0f172a", 0.04)}`,
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1}
          sx={{ minWidth: 0, flex: 1 }}
        >
          <SkillAgentInlineTitle
            entityKey={searchId}
            value={titulo}
            onChange={setTitulo}
            onCommit={async (next) => {
              if (!searchId || next === String(search?.Titulo || "").trim()) {
                return;
              }
              setBusy(true);
              setError("");
              try {
                const updated = await updateSkillAgentSearch(searchId, {
                  Titulo: next,
                });
                setSearch((p) => ({ ...p, ...updated }));
                setTitulo(updated?.Titulo || next);
                onChanged?.();
              } catch (err) {
                setError(
                  err?.response?.data?.error ||
                    t("skillAgent.search.saveError"),
                );
                setTitulo(search?.Titulo || "");
              } finally {
                setBusy(false);
              }
            }}
            placeholder={t("skillAgent.form.title")}
            disabled={busy || running}
            sx={{ maxWidth: { xs: "100%", md: 360 } }}
            inputSx={{
              fontSize: { xs: "1.05rem", md: "1.15rem" },
              lineHeight: 1.2,
            }}
          />
          <Chip
            size="small"
            label={searchStatusLabel(search.Status)}
            color={
              search.Status === "done"
                ? "success"
                : search.Status === "error"
                  ? "error"
                  : search.Status === "running"
                    ? "warning"
                    : "default"
            }
            sx={{
              height: 22,
              borderRadius: 1.25,
              fontWeight: 800,
              flexShrink: 0,
              "& .MuiChip-label": { px: 0.85, fontSize: "0.7rem" },
            }}
          />
        </Stack>

        <Stack
          direction="row"
          spacing={0.5}
          alignItems="center"
          sx={{ flexShrink: 0 }}
        >
          {search.DriveFolderUrl ? (
            <Tooltip title={t("skillAgent.search.openDriveFolder")}>
              <IconButton
                size="small"
                component="a"
                href={search.DriveFolderUrl}
                target="_blank"
                rel="noopener noreferrer"
                sx={toolBtnSx}
              >
                <OpenInNewOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title={t("skillAgent.search.paramsTooltip")}>
            <span>
              <IconButton
                size="small"
                disabled={locked}
                onClick={openEdit}
                sx={toolBtnSx}
              >
                <EditOutlinedIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={t("skillAgent.search.createMoodboardTooltip")}>
            <span>
              <Button
                size="small"
                variant="outlined"
                color="warning"
                disabled={locked || !images.length}
                startIcon={
                  creatingMoodboard ? (
                    <CircularProgress size={14} color="inherit" />
                  ) : (
                    <DashboardCustomizeOutlinedIcon />
                  )
                }
                onClick={handleCreateMoodboardFromSearch}
                sx={{
                  height: 34,
                  borderRadius: 2,
                  textTransform: "none",
                  fontWeight: 800,
                  px: 1.25,
                  whiteSpace: "nowrap",
                }}
              >
                {creatingMoodboard
                  ? t("skillAgent.search.createMoodboardBusy")
                  : t("skillAgent.search.createMoodboard")}
              </Button>
            </span>
          </Tooltip>
          {running ? (
            <Button
              size="small"
              color="warning"
              variant="outlined"
              startIcon={<StopOutlinedIcon />}
              onClick={handleCancel}
              sx={{
                height: 34,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 1.25,
              }}
            >
              {t("skillAgent.common.cancel")}
            </Button>
          ) : (
            <Button
              size="small"
              variant="contained"
              startIcon={<PlayArrowOutlinedIcon />}
              onClick={handleRun}
              disabled={locked}
              sx={{
                height: 34,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 700,
                px: 1.5,
                boxShadow: (theme) =>
                  `0 2px 10px ${alpha(theme.palette.primary.main, 0.28)}`,
              }}
            >
              {images.length
                ? t("skillAgent.search.rerun")
                : t("skillAgent.search.run")}
            </Button>
          )}
          <Tooltip title={t("skillAgent.search.deleteTooltip")}>
            <span>
              <IconButton
                size="small"
                color="error"
                disabled={locked}
                onClick={() => setDeleteOpen(true)}
                sx={{
                  ...toolBtnSx,
                  borderColor: (t) => alpha(t.palette.error.main, 0.28),
                  color: "error.main",
                  "&:hover": {
                    bgcolor: (t) => alpha(t.palette.error.main, 0.08),
                  },
                }}
              >
                <DeleteOutlineIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      <Box
        sx={{
          flex: "1 1 auto",
          minHeight: 0,
          overflowX: "hidden",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          p: { xs: 1.5, md: 2 },
          pt: 1.5,
        }}
      >
        <Stack spacing={1.5}>
          {error && (
            <Alert
              severity="error"
              onClose={() => setError("")}
              sx={{ py: 0.5 }}
            >
              {error}
            </Alert>
          )}
          {okMsg && (
            <Alert
              severity="success"
              onClose={() => setOkMsg("")}
              sx={{ py: 0.5 }}
            >
              {okMsg}
            </Alert>
          )}

          <Stack
            direction="row"
            alignItems="center"
            spacing={0.6}
            flexWrap="wrap"
            useFlexGap
          >
            {search.CustomerName ? (
              <Chip
                size="small"
                variant="outlined"
                label={search.CustomerName}
                sx={metaChipSx}
              />
            ) : null}
            {search.Temporada ? (
              <Chip
                size="small"
                variant="outlined"
                label={search.Temporada}
                sx={metaChipSx}
              />
            ) : null}
            {tipologiaShort ? (
              <Tooltip title={String(search.Tipologia || "")}>
                <Chip
                  size="small"
                  variant="outlined"
                  label={tipologiaShort}
                  sx={metaChipSx}
                />
              </Tooltip>
            ) : null}
            {runStats.nSources > 0 ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("skillAgent.search.chipSources", {
                  n: runStats.nSources,
                })}
                sx={metaChipSx}
              />
            ) : null}
            <Chip
              size="small"
              variant="outlined"
              label={t("skillAgent.search.chipMaxAge", { d: runStats.ageDays })}
              sx={metaChipSx}
            />
            <Chip
              size="small"
              variant="outlined"
              label={t("skillAgent.search.chipPerBrand", {
                n: runStats.perBrand,
              })}
              sx={metaChipSx}
            />
            {runStats.added ? (
              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={t("skillAgent.search.chipAdded", { n: runStats.added })}
                sx={{
                  ...metaChipSx,
                  bgcolor: (t) => alpha(t.palette.success.main, 0.08),
                }}
              />
            ) : null}
            {runStats.skipped ? (
              <Chip
                size="small"
                variant="outlined"
                label={t("skillAgent.search.chipSkipped", {
                  n: runStats.skipped,
                })}
                sx={metaChipSx}
              />
            ) : null}
            <Tooltip title={t("skillAgent.search.tooltipNoDuplicateDrive")}>
              <Chip
                size="small"
                variant="outlined"
                icon={<InfoOutlinedIcon sx={{ fontSize: "14px !important" }} />}
                label={t("skillAgent.search.chipNoDuplicateDrive")}
                sx={{
                  ...metaChipSx,
                  bgcolor: (t) => alpha(t.palette.info.main, 0.06),
                  borderColor: (t) => alpha(t.palette.info.main, 0.22),
                  "& .MuiChip-icon": { ml: 0.6, color: "info.main" },
                }}
              />
            </Tooltip>
          </Stack>

          {running && (
            <Stack
              direction="row"
              alignItems="center"
              spacing={1.25}
              sx={{
                px: 1.25,
                py: 0.85,
                borderRadius: 2,
                border: "1px solid",
                borderColor: (t) => alpha(t.palette.warning.main, 0.25),
                bgcolor: (t) => alpha(t.palette.warning.main, 0.06),
              }}
            >
              <CircularProgress size={16} color="warning" />
              <Typography variant="body2" fontWeight={600}>
                {t("skillAgent.search.runningBanner")}
              </Typography>
            </Stack>
          )}

          {(runStats.statuses.length > 0 || runs.length > 0) && (
            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2.25,
                overflow: "hidden",
                bgcolor: alpha("#fff", 0.55),
              }}
            >
              <Button
                fullWidth
                onClick={() => {
                  const next = !(showSourceDetail || showRuns);
                  setShowSourceDetail(next);
                  setShowRuns(next);
                }}
                endIcon={
                  showSourceDetail || showRuns ? (
                    <ExpandLessIcon />
                  ) : (
                    <ExpandMoreIcon />
                  )
                }
                startIcon={<HistoryOutlinedIcon sx={{ fontSize: 18 }} />}
                sx={{
                  justifyContent: "space-between",
                  textTransform: "none",
                  fontWeight: 700,
                  color: "text.secondary",
                  px: 1.35,
                  py: 0.7,
                  borderRadius: 0,
                  minHeight: 36,
                }}
              >
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  Run · {runStats.withHits.length} con fotos
                  {runStats.withErr.length
                    ? ` · ${runStats.withErr.length} error/SPA`
                    : ""}
                  {runs.length ? ` · historial ${runs.length}` : ""}
                </Box>
              </Button>
              <Collapse in={showSourceDetail || showRuns}>
                <Stack spacing={1.25} sx={{ px: 1.35, pb: 1.25, pt: 0.25 }}>
                  {showSourceDetail && runStats.statuses.length > 0 && (
                    <Stack
                      direction="row"
                      spacing={0.5}
                      flexWrap="wrap"
                      useFlexGap
                    >
                      {[...runStats.withHits, ...runStats.withErr]
                        .slice(0, 24)
                        .map((s) => {
                          const label =
                            s.status === "no_scrapeable"
                              ? `${s.name}: SPA`
                              : s.status === "error"
                                ? `${s.name}: error`
                                : `${s.name}: ${s.candidates || 0}→${s.passed || 0}`;
                          return (
                            <Chip
                              key={`${s.id || s.name}-${s.status}`}
                              size="small"
                              color={
                                s.status === "error" ||
                                s.status === "no_scrapeable"
                                  ? "warning"
                                  : "success"
                              }
                              variant="outlined"
                              label={label}
                              sx={{
                                height: 22,
                                borderRadius: 1.25,
                                fontSize: "0.68rem",
                                "& .MuiChip-label": { px: 0.85 },
                              }}
                            />
                          );
                        })}
                    </Stack>
                  )}
                  {showRuns &&
                    (runsLoading && !runs.length ? (
                      <Typography variant="caption" color="text.secondary">
                        {t("skillAgent.search.runsLoading")}
                      </Typography>
                    ) : !runs.length ? (
                      <Typography variant="caption" color="text.secondary">
                        {t("skillAgent.search.runsEmpty")}
                      </Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {runs.slice(0, 8).map((run) => {
                          const snap =
                            run.ParamsSnapshot &&
                            typeof run.ParamsSnapshot === "object"
                              ? run.ParamsSnapshot
                              : {};
                          const tip = String(
                            snap.tipología || search.Tipologia || "",
                          );
                          const tipShort =
                            tip.length > 42
                              ? `${tip.slice(0, 42)}…`
                              : tip || "—";
                          const when = run.createdAt
                            ? new Date(run.createdAt).toLocaleString("es-ES", {
                                dateStyle: "short",
                                timeStyle: "short",
                              })
                            : "—";
                          return (
                            <Stack
                              key={run.id}
                              direction="row"
                              alignItems="center"
                              justifyContent="space-between"
                              spacing={1}
                              sx={{
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 1.5,
                                px: 1.1,
                                py: 0.45,
                                bgcolor: alpha("#fff", 0.7),
                              }}
                            >
                              <Typography
                                variant="body2"
                                noWrap
                                sx={{ minWidth: 0 }}
                              >
                                {when} · +{run.ItemsAdded ?? 0} · {tipShort}
                              </Typography>
                              <Button
                                size="small"
                                disabled={locked || running}
                                onClick={() => handleRerun(run)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  flexShrink: 0,
                                }}
                              >
                                {t("skillAgent.search.rerun")}
                              </Button>
                            </Stack>
                          );
                        })}
                      </Stack>
                    ))}
                </Stack>
              </Collapse>
            </Box>
          )}

          <Box
            sx={{
              border: "1px solid",
              borderColor: (t) => alpha(t.palette.primary.main, 0.1),
              borderRadius: 2.5,
              overflow: "hidden",
              bgcolor: alpha("#fff", 0.72),
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              useFlexGap
              spacing={1}
              sx={{
                px: 1.35,
                py: 0.85,
                borderBottom: images.length ? "1px solid" : "none",
                borderColor: "divider",
                bgcolor: (t) => alpha(t.palette.primary.main, 0.035),
              }}
            >
              <Typography variant="subtitle2" fontWeight={800}>
                {t("skillAgent.search.photosHeader", {
                  n: images.length,
                  sel: selectedCount,
                })}
              </Typography>
              {images.length > 0 && !running && (
                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                  {selectedCount > 0 ? (
                    <>
                      <Button
                        size="small"
                        onClick={clearSelection}
                        disabled={locked}
                        sx={{ textTransform: "none", fontWeight: 700 }}
                      >
                        {t("skillAgent.search.clearSelection")}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        disabled={locked}
                        onClick={() => setSelectedDeleteOpen(true)}
                        startIcon={<DeleteOutlineIcon />}
                        sx={{
                          textTransform: "none",
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        {t("skillAgent.search.deleteSelected", {
                          n: selectedCount,
                        })}
                      </Button>
                    </>
                  ) : (
                    <Button
                      size="small"
                      disabled={locked}
                      onClick={selectAllPhotos}
                      sx={{ textTransform: "none", fontWeight: 700 }}
                    >
                      {t("skillAgent.search.selectAll")}
                    </Button>
                  )}
                  <Button
                    size="small"
                    color="warning"
                    onClick={() => setClearOpen(true)}
                    disabled={locked}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {t("skillAgent.search.deleteAllPhotos")}
                  </Button>
                </Stack>
              )}
            </Stack>

            <Box sx={{ p: 1.35 }}>
              {images.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  {t("skillAgent.search.photosEmpty")}
                </Typography>
              ) : (
                (() => {
                  const byBrand = new Map();
                  for (const img of images) {
                    const key =
                      img.BrandName ||
                      img.BrandId ||
                      t("skillAgent.search.noBrandGroup");
                    if (!byBrand.has(key)) byBrand.set(key, []);
                    byBrand.get(key).push(img);
                  }
                  const brandEntries = [...byBrand.entries()].sort((a, b) =>
                    String(a[0]).localeCompare(String(b[0]), "es", {
                      sensitivity: "base",
                    }),
                  );
                  return (
                    <Stack spacing={1.75}>
                      {brandEntries.map(([brand, imgs]) => (
                        <Box key={brand}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            spacing={0.75}
                            sx={{ mb: 0.85 }}
                          >
                            <Typography
                              variant="caption"
                              fontWeight={800}
                              color="text.secondary"
                              sx={{
                                letterSpacing: 0.04,
                                textTransform: "uppercase",
                              }}
                            >
                              {brand}
                            </Typography>
                            <Chip
                              size="small"
                              label={imgs.length}
                              sx={{
                                height: 18,
                                minWidth: 22,
                                borderRadius: 1,
                                fontWeight: 800,
                                fontSize: "0.65rem",
                                bgcolor: (t) =>
                                  alpha(t.palette.primary.main, 0.1),
                                "& .MuiChip-label": { px: 0.6 },
                              }}
                            />
                          </Stack>
                          <Box
                            sx={{
                              display: "grid",
                              gridTemplateColumns:
                                "repeat(auto-fill, minmax(132px, 1fr))",
                              gap: 1.1,
                            }}
                          >
                            {imgs.map((img) => (
                              <SearchThumb
                                key={img.id}
                                item={img}
                                selectable={!running}
                                selected={selectedIds.has(Number(img.id))}
                                onToggleSelect={toggleSelect}
                                onOpen={(it) => {
                                  const idx = images.findIndex(
                                    (x) => Number(x.id) === Number(it.id),
                                  );
                                  if (idx >= 0) setLightboxIndex(idx);
                                }}
                              />
                            ))}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  );
                })()
              )}
            </Box>
          </Box>

          {/* Edit params — misma familia visual Compás (blur + rise + stage). */}
          <Dialog
            open={editOpen}
            onClose={() => !busy && setEditOpen(false)}
            fullWidth
            maxWidth="md"
            keepMounted={false}
            transitionDuration={180}
            slotProps={{
              backdrop: { sx: studioDialogBackdropSx },
            }}
            BackdropProps={{ sx: studioDialogBackdropSx }}
            PaperProps={{
              sx: {
                ...studioDialogPaperSx,
                animation: editOpen ? `${rise} 280ms ease both` : undefined,
                ...motionSafe,
              },
            }}
          >
            <Box
              sx={{
                px: 2.5,
                pt: 2,
                pb: 1.25,
                borderBottom: "1px solid",
                borderColor: "divider",
                background: `linear-gradient(135deg, ${alpha(SEARCH_ACCENT, 0.14)} 0%, ${alpha(
                  SEARCH_ACCENT,
                  0.02,
                )} 55%)`,
                animation: `${rise} 300ms ease both`,
                ...motionSafe,
              }}
            >
              <Typography
                variant="overline"
                sx={{ color: SEARCH_ACCENT, fontWeight: 800 }}
              >
                {t("skillAgent.search.editDialogEyebrow", {
                  defaultValue: "Compás · SEARCH",
                })}
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontWeight: 800, lineHeight: 1.2 }}
              >
                {t("skillAgent.search.editDialogTitle")}
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mt: 0.5 }}
              >
                {[search?.CustomerName, search?.Temporada]
                  .filter(Boolean)
                  .join(" · ") ||
                  t("skillAgent.search.editDialogLead", {
                    defaultValue:
                      "Ajusta tipología, marcas y fuentes antes de volver a lanzar.",
                  })}
              </Typography>
              <SearchParamsStage accent={SEARCH_ACCENT} />
            </Box>
            <DialogContent sx={{ pt: 2, px: 2.5 }}>
              <Stack spacing={1.5}>
                <TextField
                  size="small"
                  label={t("skillAgent.form.title")}
                  value={editForm.Titulo}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, Titulo: e.target.value }))
                  }
                  fullWidth
                  sx={{
                    ...pageFieldSx,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: isDark
                        ? alpha("#fff", 0.04)
                        : alpha("#000", 0.02),
                    },
                  }}
                />
                <TextField
                  size="small"
                  label={t("skillAgent.form.tipologyKeywords")}
                  value={editForm.Tipologia}
                  onChange={(e) =>
                    setEditForm((p) => ({ ...p, Tipologia: e.target.value }))
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  helperText={t("skillAgent.form.tipologyHelper")}
                  sx={{
                    ...pageFieldSx,
                    "& .MuiOutlinedInput-root": {
                      bgcolor: isDark
                        ? alpha("#fff", 0.04)
                        : alpha("#000", 0.02),
                    },
                  }}
                />
                <Stack direction="row" spacing={1.5}>
                  <TextField
                    size="small"
                    label={t("skillAgent.form.imgsPerBrand")}
                    type="number"
                    inputProps={{ min: 1, max: 12 }}
                    value={editForm.maxImagesPerBrand}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        maxImagesPerBrand: e.target.value,
                      }))
                    }
                    sx={{
                      width: 120,
                      ...pageFieldSx,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: isDark
                          ? alpha("#fff", 0.04)
                          : alpha("#000", 0.02),
                      },
                    }}
                  />
                  <TextField
                    size="small"
                    label={t("skillAgent.form.maxAgeDays")}
                    type="number"
                    inputProps={{ min: 7, max: 365 }}
                    value={editForm.maxAgeDays}
                    onChange={(e) =>
                      setEditForm((p) => ({
                        ...p,
                        maxAgeDays: e.target.value,
                      }))
                    }
                    helperText={t("skillAgent.form.maxAgeDaysHelper")}
                    sx={{
                      width: 150,
                      ...pageFieldSx,
                      "& .MuiOutlinedInput-root": {
                        bgcolor: isDark
                          ? alpha("#fff", 0.04)
                          : alpha("#000", 0.02),
                      },
                    }}
                  />
                </Stack>
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  flexWrap="wrap"
                  useFlexGap
                  spacing={1}
                  sx={{ mb: 0.5 }}
                >
                  <Typography variant="subtitle2" fontWeight={800}>
                    {t("skillAgent.search.clientBrands")}
                  </Typography>
                  <Button
                    size="small"
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      color: SEARCH_ACCENT,
                    }}
                    disabled={!search?.CustomerId}
                    onClick={() => setCatalogOpen(true)}
                  >
                    {t("skillAgent.search.manageCatalog")}
                  </Button>
                </Stack>
                <SkillAgentBrandSourcesFields
                  brands={catalogBrands}
                  brandIds={editForm.brandIds}
                  urlSelection={editForm.urlSelection}
                  tipologia={editForm.Tipologia}
                  onBrandIdsChange={(brandIds) =>
                    setEditForm((p) => ({ ...p, brandIds }))
                  }
                  onUrlSelectionChange={(urlSelection) =>
                    setEditForm((p) => ({ ...p, urlSelection }))
                  }
                />
                <SkillAgentCustomSourcesFields
                  sources={editForm.customSources}
                  tipologia={editForm.Tipologia}
                  onChange={(customSources) =>
                    setEditForm((p) => ({ ...p, customSources }))
                  }
                />
                <Typography variant="caption" color="text.secondary">
                  {t("skillAgent.search.editDialogFootnote", {
                    defaultValue:
                      "Novedades (días) solo aplica si la web da fecha (p. ej. Shopify). Las webs one-off van abajo; lo estable mejor en el catálogo.",
                  })}
                </Typography>
              </Stack>
            </DialogContent>
            <DialogActions
              sx={{ px: 2.5, pb: 2, pt: 0.5, flexWrap: "wrap", gap: 1 }}
            >
              <Button
                onClick={() => setEditOpen(false)}
                disabled={busy}
                color="inherit"
              >
                {t("skillAgent.common.close")}
              </Button>
              <Button
                disabled={busy}
                onClick={() => handleSaveParams({ andRun: false })}
                sx={{ fontWeight: 700, color: SEARCH_ACCENT }}
              >
                {t("skillAgent.common.save")}
              </Button>
              <Button
                variant="contained"
                sx={pagePrimaryButtonSx}
                disabled={busy}
                startIcon={
                  busy ? (
                    <CircularProgress size={16} color="inherit" />
                  ) : (
                    <PlayArrowOutlinedIcon />
                  )
                }
                onClick={() => handleSaveParams({ andRun: true })}
              >
                {t("skillAgent.search.saveAndRerun")}
              </Button>
            </DialogActions>
          </Dialog>

          <SearchConfirmDialog
            open={clearOpen}
            onClose={() => setClearOpen(false)}
            accent={WARN_ACCENT}
            confirmColor="warning"
            eyebrow={t("skillAgent.search.editDialogEyebrow", {
              defaultValue: "Compás · SEARCH",
            })}
            title={t("skillAgent.search.clearDialogTitle")}
            body={t("skillAgent.search.clearDialogBody")}
            countLabel={
              images.length
                ? t("skillAgent.search.clearDialogCount", { n: images.length })
                : null
            }
            cancelLabel={t("skillAgent.common.cancel")}
            confirmLabel={t("skillAgent.search.deleteAllPhotos")}
            busy={busy}
            onConfirm={handleClearPhotos}
          />

          <SearchConfirmDialog
            open={selectedDeleteOpen}
            onClose={() => setSelectedDeleteOpen(false)}
            accent={DANGER_ACCENT}
            eyebrow={t("skillAgent.search.editDialogEyebrow", {
              defaultValue: "Compás · SEARCH",
            })}
            title={t("skillAgent.search.deleteSelectedDialogTitle")}
            body={t("skillAgent.search.deleteSelectedBody")}
            countLabel={t("skillAgent.search.clearDialogCount", {
              n: selectedCount,
            })}
            cancelLabel={t("skillAgent.common.cancel")}
            confirmLabel={t("skillAgent.search.deleteSelected", {
              n: selectedCount,
            })}
            busy={busy}
            confirmDisabled={selectedCount === 0}
            onConfirm={handleDeleteSelected}
          />

          <SearchConfirmDialog
            open={deleteOpen}
            onClose={() => setDeleteOpen(false)}
            accent={DANGER_ACCENT}
            eyebrow={t("skillAgent.search.editDialogEyebrow", {
              defaultValue: "Compás · SEARCH",
            })}
            title={t("skillAgent.search.deleteDialogTitle")}
            body={t("skillAgent.search.deleteDialogBody")}
            cancelLabel={t("skillAgent.common.cancel")}
            confirmLabel={t("skillAgent.search.deleteSubmit")}
            busy={busy}
            onConfirm={handleDelete}
          />

          <SearchPhotoLightbox
            items={images}
            index={lightboxIndex}
            onClose={() => setLightboxIndex(null)}
            onIndexChange={setLightboxIndex}
            onDeleteCurrent={running ? undefined : handleDeleteLightboxItem}
            deleting={busy}
          />

          <SkillAgentResearchCatalogDialog
            open={catalogOpen}
            onClose={() => setCatalogOpen(false)}
            customerId={search?.CustomerId}
            customerName={search?.CustomerName}
            onChanged={() => {
              const customerId = search?.CustomerId;
              getSkillAgentSearchesMeta(
                customerId ? { customerId: Number(customerId) } : {},
              )
                .then((m) => {
                  const list = Array.isArray(m?.brands) ? m.brands : [];
                  setCatalogBrands(
                    [...list].sort((a, b) =>
                      String(a.name || a.id).localeCompare(
                        String(b.name || b.id),
                        "es",
                        { sensitivity: "base" },
                      ),
                    ),
                  );
                })
                .catch(() => {});
            }}
          />
        </Stack>
      </Box>
    </Box>
  );
}
